import { DemographicQuestionId } from '../../../pure-js/types/Demographics'

export interface DemographicOption {
  value: number
  labelKey: string
  hoverKey?: string
}

export interface DemographicQuestionConfig {
  id: DemographicQuestionId
  titleKey: string
  type: 'choice' | 'country'
  tooltipKey: string
  verticalOptions?: boolean
  options?: DemographicOption[]
}

// Q1: Age
// Q2: Gender
// Q3: Country of residence
// Q4: Role level (value 1 = IC — triggers Q5 hidden with team_size=0)
// Q5: Team size (hidden when Q4=1)
// Q6: Gaming experience
// Q7: Years of experience

export const DEMOGRAPHIC_QUESTIONS: DemographicQuestionConfig[] = [
  {
    id: 'demographic_age',
    titleKey: 'gamer.demographics.questions.age.title',
    tooltipKey: 'gamer.demographics.questions.age.tooltip',
    type: 'choice',
    options: [
      { value: 1, labelKey: 'gamer.demographics.questions.age.options.under25' },
      { value: 2, labelKey: 'gamer.demographics.questions.age.options.25to34' },
      { value: 3, labelKey: 'gamer.demographics.questions.age.options.35to44' },
      { value: 4, labelKey: 'gamer.demographics.questions.age.options.45to54' },
      { value: 5, labelKey: 'gamer.demographics.questions.age.options.55to64' },
      { value: 6, labelKey: 'gamer.demographics.questions.age.options.65plus' }
    ]
  },
  {
    id: 'demographic_gender',
    titleKey: 'gamer.demographics.questions.gender.title',
    tooltipKey: 'gamer.demographics.questions.gender.tooltip',
    type: 'choice',
    options: [
      { value: 1, labelKey: 'gamer.demographics.questions.gender.options.male' },
      { value: 2, labelKey: 'gamer.demographics.questions.gender.options.female' },
      { value: 3, labelKey: 'gamer.demographics.questions.gender.options.nonBinary' },
      { value: 4, labelKey: 'gamer.demographics.questions.gender.options.preferNotToSay' }
    ]
  },
  {
    id: 'demographic_country_of_residence',
    titleKey: 'gamer.demographics.questions.country.title',
    tooltipKey: 'gamer.demographics.questions.country.tooltip',
    type: 'country'
  },
  {
    id: 'demographic_role_level',
    titleKey: 'gamer.demographics.questions.roleLevel.title',
    tooltipKey: 'gamer.demographics.questions.roleLevel.tooltip',
    type: 'choice',
    verticalOptions: true,
    options: [
      { value: 1, labelKey: 'gamer.demographics.questions.roleLevel.options.ic' },
      { value: 2, labelKey: 'gamer.demographics.questions.roleLevel.options.teamLead' },
      { value: 3, labelKey: 'gamer.demographics.questions.roleLevel.options.middleManager' },
      { value: 4, labelKey: 'gamer.demographics.questions.roleLevel.options.seniorManager' },
      { value: 5, labelKey: 'gamer.demographics.questions.roleLevel.options.executive' },
      { value: 6, labelKey: 'gamer.demographics.questions.roleLevel.options.other' }
    ]
  },
  {
    id: 'demographic_team_size',
    titleKey: 'gamer.demographics.questions.teamSize.title',
    tooltipKey: 'gamer.demographics.questions.teamSize.tooltip',
    type: 'choice',
    options: [
      { value: 1, labelKey: 'gamer.demographics.questions.teamSize.options.1to3' },
      { value: 2, labelKey: 'gamer.demographics.questions.teamSize.options.4to7' },
      { value: 3, labelKey: 'gamer.demographics.questions.teamSize.options.8to15' },
      { value: 4, labelKey: 'gamer.demographics.questions.teamSize.options.16to30' },
      { value: 5, labelKey: 'gamer.demographics.questions.teamSize.options.30plus' }
    ]
  },
  {
    id: 'demographic_gaming_experience',
    titleKey: 'gamer.demographics.questions.gamingExperience.title',
    tooltipKey: 'gamer.demographics.questions.gamingExperience.tooltip',
    type: 'choice',
    options: [
      {
        value: 1,
        labelKey: 'gamer.demographics.questions.gamingExperience.options.none',
        hoverKey: 'gamer.demographics.questions.gamingExperience.hover.none'
      },
      {
        value: 2,
        labelKey: 'gamer.demographics.questions.gamingExperience.options.casual',
        hoverKey: 'gamer.demographics.questions.gamingExperience.hover.casual'
      },
      {
        value: 3,
        labelKey: 'gamer.demographics.questions.gamingExperience.options.regular',
        hoverKey: 'gamer.demographics.questions.gamingExperience.hover.regular'
      },
      {
        value: 4,
        labelKey: 'gamer.demographics.questions.gamingExperience.options.frequent',
        hoverKey: 'gamer.demographics.questions.gamingExperience.hover.frequent'
      }
    ]
  },
  {
    id: 'demographic_years_experience',
    titleKey: 'gamer.demographics.questions.yearsExperience.title',
    tooltipKey: 'gamer.demographics.questions.yearsExperience.tooltip',
    type: 'choice',
    options: [
      { value: 1, labelKey: 'gamer.demographics.questions.yearsExperience.options.under2' },
      { value: 2, labelKey: 'gamer.demographics.questions.yearsExperience.options.2to5' },
      { value: 3, labelKey: 'gamer.demographics.questions.yearsExperience.options.6to10' },
      { value: 4, labelKey: 'gamer.demographics.questions.yearsExperience.options.11to15' },
      { value: 5, labelKey: 'gamer.demographics.questions.yearsExperience.options.16to25' },
      { value: 6, labelKey: 'gamer.demographics.questions.yearsExperience.options.25plus' }
    ]
  }
]

export const IC_ROLE_VALUE = 1
export const OTHER_ROLE_VALUE = 6
export const IC_TEAM_SIZE_VALUE = 0

// Countries list — pinned countries use the same numeric codes as in COUNTRIES.
// Pinned values: Sweden=168, Denmark=47, Germany=65
export const PINNED_COUNTRY_VALUES = [168, 47, 65] as const

export interface CountryOption {
  label: string
  value: number
  pinned?: boolean
}

export const COUNTRIES: CountryOption[] = [
  { label: 'Afghanistan', value: 1 },
  { label: 'Albania', value: 2 },
  { label: 'Algeria', value: 3 },
  { label: 'Andorra', value: 4 },
  { label: 'Angola', value: 5 },
  { label: 'Antigua and Barbuda', value: 6 },
  { label: 'Argentina', value: 7 },
  { label: 'Armenia', value: 8 },
  { label: 'Australia', value: 9 },
  { label: 'Austria', value: 10 },
  { label: 'Azerbaijan', value: 11 },
  { label: 'Bahamas', value: 12 },
  { label: 'Bahrain', value: 13 },
  { label: 'Bangladesh', value: 14 },
  { label: 'Barbados', value: 15 },
  { label: 'Belarus', value: 16 },
  { label: 'Belgium', value: 17 },
  { label: 'Belize', value: 18 },
  { label: 'Benin', value: 19 },
  { label: 'Bhutan', value: 20 },
  { label: 'Bolivia', value: 21 },
  { label: 'Bosnia and Herzegovina', value: 22 },
  { label: 'Botswana', value: 23 },
  { label: 'Brazil', value: 24 },
  { label: 'Brunei', value: 25 },
  { label: 'Bulgaria', value: 26 },
  { label: 'Burkina Faso', value: 27 },
  { label: 'Burundi', value: 28 },
  { label: 'Cabo Verde', value: 29 },
  { label: 'Cambodia', value: 30 },
  { label: 'Cameroon', value: 31 },
  { label: 'Canada', value: 32 },
  { label: 'Central African Republic', value: 33 },
  { label: 'Chad', value: 34 },
  { label: 'Chile', value: 35 },
  { label: 'China', value: 36 },
  { label: 'Colombia', value: 37 },
  { label: 'Comoros', value: 38 },
  { label: 'Congo (Brazzaville)', value: 39 },
  { label: 'Congo (Kinshasa)', value: 40 },
  { label: 'Costa Rica', value: 41 },
  { label: "Côte d'Ivoire", value: 42 },
  { label: 'Croatia', value: 43 },
  { label: 'Cuba', value: 44 },
  { label: 'Cyprus', value: 45 },
  { label: 'Czech Republic', value: 46 },
  { label: 'Denmark', value: 47 },
  { label: 'Djibouti', value: 48 },
  { label: 'Dominica', value: 49 },
  { label: 'Dominican Republic', value: 50 },
  { label: 'Ecuador', value: 51 },
  { label: 'Egypt', value: 52 },
  { label: 'El Salvador', value: 53 },
  { label: 'Equatorial Guinea', value: 54 },
  { label: 'Eritrea', value: 55 },
  { label: 'Estonia', value: 56 },
  { label: 'Eswatini', value: 57 },
  { label: 'Ethiopia', value: 58 },
  { label: 'Fiji', value: 59 },
  { label: 'Finland', value: 60 },
  { label: 'France', value: 61 },
  { label: 'Gabon', value: 62 },
  { label: 'Gambia', value: 63 },
  { label: 'Georgia', value: 64 },
  { label: 'Germany', value: 65 },
  { label: 'Ghana', value: 66 },
  { label: 'Greece', value: 67 },
  { label: 'Grenada', value: 68 },
  { label: 'Guatemala', value: 69 },
  { label: 'Guinea', value: 70 },
  { label: 'Guinea-Bissau', value: 71 },
  { label: 'Guyana', value: 72 },
  { label: 'Haiti', value: 73 },
  { label: 'Honduras', value: 74 },
  { label: 'Hungary', value: 75 },
  { label: 'Iceland', value: 76 },
  { label: 'India', value: 77 },
  { label: 'Indonesia', value: 78 },
  { label: 'Iran', value: 79 },
  { label: 'Iraq', value: 80 },
  { label: 'Ireland', value: 81 },
  { label: 'Israel', value: 82 },
  { label: 'Italy', value: 83 },
  { label: 'Jamaica', value: 84 },
  { label: 'Japan', value: 85 },
  { label: 'Jordan', value: 86 },
  { label: 'Kazakhstan', value: 87 },
  { label: 'Kenya', value: 88 },
  { label: 'Kiribati', value: 89 },
  { label: 'Kuwait', value: 90 },
  { label: 'Kyrgyzstan', value: 91 },
  { label: 'Laos', value: 92 },
  { label: 'Latvia', value: 93 },
  { label: 'Lebanon', value: 94 },
  { label: 'Lesotho', value: 95 },
  { label: 'Liberia', value: 96 },
  { label: 'Libya', value: 97 },
  { label: 'Liechtenstein', value: 98 },
  { label: 'Lithuania', value: 99 },
  { label: 'Luxembourg', value: 100 },
  { label: 'Madagascar', value: 101 },
  { label: 'Malawi', value: 102 },
  { label: 'Malaysia', value: 103 },
  { label: 'Maldives', value: 104 },
  { label: 'Mali', value: 105 },
  { label: 'Malta', value: 106 },
  { label: 'Marshall Islands', value: 107 },
  { label: 'Mauritania', value: 108 },
  { label: 'Mauritius', value: 109 },
  { label: 'Mexico', value: 110 },
  { label: 'Micronesia', value: 111 },
  { label: 'Moldova', value: 112 },
  { label: 'Monaco', value: 113 },
  { label: 'Mongolia', value: 114 },
  { label: 'Montenegro', value: 115 },
  { label: 'Morocco', value: 116 },
  { label: 'Mozambique', value: 117 },
  { label: 'Myanmar', value: 118 },
  { label: 'Namibia', value: 119 },
  { label: 'Nauru', value: 120 },
  { label: 'Nepal', value: 121 },
  { label: 'Netherlands', value: 122 },
  { label: 'New Zealand', value: 123 },
  { label: 'Nicaragua', value: 124 },
  { label: 'Niger', value: 125 },
  { label: 'Nigeria', value: 126 },
  { label: 'North Korea', value: 127 },
  { label: 'North Macedonia', value: 128 },
  { label: 'Norway', value: 129 },
  { label: 'Oman', value: 130 },
  { label: 'Pakistan', value: 131 },
  { label: 'Palau', value: 132 },
  { label: 'Palestine', value: 133 },
  { label: 'Panama', value: 134 },
  { label: 'Papua New Guinea', value: 135 },
  { label: 'Paraguay', value: 136 },
  { label: 'Peru', value: 137 },
  { label: 'Philippines', value: 138 },
  { label: 'Poland', value: 139 },
  { label: 'Portugal', value: 140 },
  { label: 'Qatar', value: 141 },
  { label: 'Romania', value: 142 },
  { label: 'Russia', value: 143 },
  { label: 'Rwanda', value: 144 },
  { label: 'Saint Kitts and Nevis', value: 145 },
  { label: 'Saint Lucia', value: 146 },
  { label: 'Saint Vincent and the Grenadines', value: 147 },
  { label: 'Samoa', value: 148 },
  { label: 'San Marino', value: 149 },
  { label: 'Sao Tome and Principe', value: 150 },
  { label: 'Saudi Arabia', value: 151 },
  { label: 'Senegal', value: 152 },
  { label: 'Serbia', value: 153 },
  { label: 'Seychelles', value: 154 },
  { label: 'Sierra Leone', value: 155 },
  { label: 'Singapore', value: 156 },
  { label: 'Slovakia', value: 157 },
  { label: 'Slovenia', value: 158 },
  { label: 'Solomon Islands', value: 159 },
  { label: 'Somalia', value: 160 },
  { label: 'South Africa', value: 161 },
  { label: 'South Korea', value: 162 },
  { label: 'South Sudan', value: 163 },
  { label: 'Spain', value: 164 },
  { label: 'Sri Lanka', value: 165 },
  { label: 'Sudan', value: 166 },
  { label: 'Suriname', value: 167 },
  { label: 'Sweden', value: 168 },
  { label: 'Switzerland', value: 169 },
  { label: 'Syria', value: 170 },
  { label: 'Taiwan', value: 171 },
  { label: 'Tajikistan', value: 172 },
  { label: 'Tanzania', value: 173 },
  { label: 'Thailand', value: 174 },
  { label: 'Timor-Leste', value: 175 },
  { label: 'Togo', value: 176 },
  { label: 'Tonga', value: 177 },
  { label: 'Trinidad and Tobago', value: 178 },
  { label: 'Tunisia', value: 179 },
  { label: 'Turkey', value: 180 },
  { label: 'Turkmenistan', value: 181 },
  { label: 'Tuvalu', value: 182 },
  { label: 'Uganda', value: 183 },
  { label: 'Ukraine', value: 184 },
  { label: 'United Arab Emirates', value: 185 },
  { label: 'United Kingdom', value: 186 },
  { label: 'United States', value: 187 },
  { label: 'Uruguay', value: 188 },
  { label: 'Uzbekistan', value: 189 },
  { label: 'Vanuatu', value: 190 },
  { label: 'Vatican City', value: 191 },
  { label: 'Venezuela', value: 192 },
  { label: 'Vietnam', value: 193 },
  { label: 'Yemen', value: 194 },
  { label: 'Zambia', value: 195 },
  { label: 'Zimbabwe', value: 196 }
]

// Pinned country objects (same value as in COUNTRIES, so Firebase writes are consistent)
export const PINNED_COUNTRIES: CountryOption[] = COUNTRIES.filter((c) =>
  (PINNED_COUNTRY_VALUES as readonly number[]).includes(c.value)
)
  .map((c) => ({ ...c, pinned: true }))
  // maintain the order: Sweden, Denmark, Germany
  .sort((a, b) => {
    const ia = (PINNED_COUNTRY_VALUES as readonly number[]).indexOf(a.value)
    const ib = (PINNED_COUNTRY_VALUES as readonly number[]).indexOf(b.value)
    return ia - ib
  })

// Non-pinned countries (exclude pinned ones to avoid duplicates when not typing)
export const UNPINNED_COUNTRIES: CountryOption[] = COUNTRIES.filter(
  (c) => !(PINNED_COUNTRY_VALUES as readonly number[]).includes(c.value)
)

// All countries for autocomplete: pinned first, then the rest alphabetical
export const ALL_COUNTRIES_FOR_SELECT: CountryOption[] = [...PINNED_COUNTRIES, ...UNPINNED_COUNTRIES]
